import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  PasswordInput,
  Button,
  Title,
  Text,
  Image,
  Stack,
  Alert,
  Anchor,
  Avatar,
  Group,
  Divider,
  Skeleton,
  Badge,
} from "@mantine/core";

import {
  IconAlertCircle,
  IconCheck,
  IconUser,
  IconKey,
  IconUserCheck,
} from "@tabler/icons-react";
import { authService } from "../services";
import { validatePassword } from "../utils/passwordValidation";
import PasswordStrengthIndicator from "../components/common/PasswordStrengthIndicator";

const GCP_LOGO_URL =
  "https://i0.wp.com/gulfcoastpath.com/wp-content/uploads/2021/02/cropped-GCP-Logo-v1.png?fit=1628%2C1071&ssl=1";

type TokenInfo = {
  fullName: string;
  username: string;
  invitationStatus: string;
  roleName: string | null;
  facilityName: string | null;
  invitedByName: string | null;
  invitedByEmail: string | null;
  portalUrl: string;
};

// Derive which "flow" we're in from the user's invitation status
function getFlowCopy(invitationStatus: string | undefined) {
  const isActivation =
    invitationStatus === "not_invited" || invitationStatus === "invited";

  return {
    isActivation,
    pageTitle: isActivation ? "Activate Your Account" : "Set New Password",
    pageSubtitle: isActivation
      ? "Create a password to activate your account"
      : "Enter your new password below",
    buttonLabel: isActivation ? "Activate Account" : "Reset Password",
    successMessage: isActivation
      ? "Your account has been activated. Redirecting to login..."
      : "Your password has been reset successfully. Redirecting to login...",
    badge: isActivation
      ? { label: "Account Activation", color: "teal" }
      : { label: "Password Reset", color: "gcpBlue" },
    icon: isActivation ? IconUserCheck : IconKey,
  };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [tokenInfoLoading, setTokenInfoLoading] = useState(true);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (!token) return;
    setTokenInfoLoading(true);
    authService
      .getResetTokenInfo(token)
      .then((info) => {
        setTokenInfo(info);
        setTokenInvalid(false);
      })
      .catch(() => setTokenInvalid(true))
      .finally(() => setTokenInfoLoading(false));
  }, [token]);

  const flow = getFlowCopy(tokenInfo?.invitationStatus);

  const { valid: passwordValid } = validatePassword(newPassword);
  const passwordsMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!passwordValid) {
      setError("Password does not meet complexity requirements");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Invalid link. Please request a new one.");
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword({ token, newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : "Failed. The link may have expired.";

      if (
        errorMessage.toLowerCase().includes("expired") ||
        errorMessage.toLowerCase().includes("invalid")
      ) {
        setError("This link has expired. You can request a new one below.");
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const invalidLinkScreen = (
    message: string,
    action: { label: string; to: string }
  ) => (
    <Container size={420} my={40}>
      <Stack align="center" mb="xl">
        <Image src={GCP_LOGO_URL} alt="GCP" h={80} fit="contain" />
        <Title order={2} c="gcpBlue">
          Invalid Link
        </Title>
      </Stack>
      <Paper withBorder shadow="md" p={30} radius="md">
        <Stack>
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {message}
          </Alert>
          <Anchor component={Link} to={action.to} size="sm" ta="center">
            {action.label}
          </Anchor>
        </Stack>
      </Paper>
    </Container>
  );

  if (!token)
    return invalidLinkScreen(
      "This link is invalid. Please request a new one.",
      {
        label: "Request new reset link",
        to: "/forgot-password",
      }
    );
  if (!tokenInfoLoading && tokenInvalid)
    return invalidLinkScreen(
      "This link has already been used. Please log in to your account.",
      { label: "Go to login", to: "/login" }
    );

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="xl">
        <Image src={GCP_LOGO_URL} alt="GCP" h={80} fit="contain" />
        <Title order={2} c="gcpBlue">
          {tokenInfoLoading ? (
            <Skeleton height={28} width={200} radius="sm" />
          ) : (
            flow.pageTitle
          )}
        </Title>
        <Text c="dimmed" size="sm">
          {tokenInfoLoading ? (
            <Skeleton height={14} width={160} radius="sm" />
          ) : (
            flow.pageSubtitle
          )}
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        {success ? (
          <Stack>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              {flow.successMessage}
            </Alert>
            <Anchor component={Link} to="/login" size="sm" ta="center">
              Go to login now
            </Anchor>
          </Stack>
        ) : (
          <form
            onSubmit={(e) => {
              handleSubmit(e).catch(() => {});
            }}
          >
            <Stack>
              {/* User info card */}
              {tokenInfoLoading ? (
                <Group gap="sm">
                  <Skeleton height={40} circle />
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Skeleton height={14} width="60%" radius="sm" />
                    <Skeleton height={12} width="40%" radius="sm" />
                  </Stack>
                </Group>
              ) : tokenInfo ? (
                <>
                  <Group gap="sm" justify="space-between" align="flex-start">
                    <Group gap="sm">
                      <Avatar color="gcpBlue" radius="xl" size="md">
                        <IconUser size={20} />
                      </Avatar>
                      <Stack gap={2}>
                        <Text fw={600} size="sm" lh={1.3}>
                          {tokenInfo.fullName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          @{tokenInfo.username}
                        </Text>
                      </Stack>
                    </Group>
                    <Badge
                      color={flow.badge.color}
                      variant="light"
                      size="sm"
                      leftSection={<flow.icon size={11} />}
                    >
                      {flow.badge.label}
                    </Badge>
                  </Group>
                  <Divider />
                </>
              ) : null}

              {error && (
                <Stack gap="xs">
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                  >
                    {error}
                  </Alert>
                  {error.includes("expired") && (
                    <Anchor
                      component={Link}
                      to="/forgot-password"
                      size="sm"
                      ta="center"
                    >
                      Request a new reset link
                    </Anchor>
                  )}
                </Stack>
              )}

              <PasswordInput
                label="New Password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />

              <PasswordStrengthIndicator password={newPassword} />

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                error={
                  confirmPassword && !passwordsMatch
                    ? "Passwords do not match"
                    : undefined
                }
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!canSubmit}
              >
                {flow.buttonLabel}
              </Button>

              <Anchor component={Link} to="/login" size="sm" ta="center">
                Back to login
              </Anchor>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
}
